/**
 * mergeBrackets — high-level glue: JPEG/PNG blobs → fused ACEScg Float32.
 *
 * Pipeline per blob (DECODE is sequential — one ImageBitmap +
 * OffscreenCanvas alive at a time):
 *
 *   Blob → ImageBitmap → OffscreenCanvas getImageData (Uint8 sRGB)
 *        → ingestSRGB → Float32 ACEScg-linear → push into stack[]
 *
 * After all brackets are ingested:
 *
 *   estimateEVStops / parseExifEV → applyEV per bracket (in-place on
 *   internal stack — caller's Float32 buffers are cloned at entry,
 *   never mutated).
 *   fuseBrackets(stack) → Float32 ACEScg, alpha = 1.0
 *
 * Memory profile (Codex HDR-A review correction):
 *   Decode is sequential, so we never hold three SOURCE Uint8 buffers in
 *   parallel. BUT: the full Float32 stack stays alive through fusion,
 *   plus during fusion we hold (merged pyramid + one bracket's gauss/
 *   laplacian pyramids + one weight gauss pyramid) on top of stack.
 *   For 3×24 MP this peaks around 1.1 GB in practice — well over the
 *   350 MB target. Tile-based fuse (cf. docs/HDR-DESIGN.md "Tonight's
 *   scope") is required to hit that ceiling and is NOT yet implemented.
 *
 * The wrapper exists so callers (UI, AI worker, future RAW path) can
 * pass blobs without re-implementing decode + ingest + EV-normalise
 * boilerplate.
 */

import { ingestSRGB }       from '../ingest.js';
import { fuseBrackets }     from './mertens.js';
import { estimateEVStops, parseExifEV } from './exposure.js';
import { detectFusionMode } from './auto-detect.js';

const SUPPORTED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png']);

function isBlobLike(x) {
    return x && typeof x.arrayBuffer === 'function' && typeof x.size === 'number';
}
function isImageDataLike(x) {
    return x && typeof x.width === 'number' && typeof x.height === 'number'
        && (x.data instanceof Uint8ClampedArray
            || x.data instanceof Uint8Array
            || x.rgba instanceof Float32Array);
}

/**
 * @typedef {object} MergeResult
 * @prop {Float32Array} rgba   width*height*4, ACEScg-linear, alpha=1
 * @prop {number} width
 * @prop {number} height
 * @prop {Float32Array} evStops  EV offsets used (relative to median bracket)
 * @prop {string} source       'jpeg' | 'png' | 'mixed'
 */

/**
 * @typedef {object} MergeOpts
 * @prop {number} [levels=5]                   pyramid depth, [3..7]
 * @prop {number} [contrast=1]
 * @prop {number} [saturation=1]
 * @prop {number} [exposure=1]
 * @prop {boolean} [useExif=true]              prefer EXIF EVs over pixel-median estimate
 * @prop {AbortSignal} [signal]                cancel between decode/ingest/levels
 */

/**
 * Merge a list of bracketed inputs into a single fused ACEScg Float32 image.
 *
 * Polymorphic input — the caller passes EITHER:
 *   • an array of Blob (JPEG/PNG) — each decoded sequentially via createImageBitmap
 *   • an array of ImageData-like objects: { width, height, data } where `data`
 *     is a Uint8ClampedArray of sRGB RGBA, OR `rgba` is a Float32Array already
 *     in ACEScg-linear space (e.g. produced by an upstream pipeline stage).
 * Mixing within one call is NOT supported — pick a single shape per call.
 *
 * Sequential processing: at most one source bracket is held in Uint8 form
 * at a time. Float32 stacks of 2-3 brackets are kept simultaneously because
 * they are required by the fusion math (Laplacian pyramid of each bracket).
 *
 * @param {Blob[] | Array<{width:number,height:number,data?:Uint8ClampedArray|Uint8Array,rgba?:Float32Array}>} input
 * @param {MergeOpts} [opts]
 * @returns {Promise<MergeResult>}
 */
export async function mergeBrackets(input, opts = {}) {
    if (!Array.isArray(input) || input.length < 2) {
        throw new Error('mergeBrackets: need at least 2 brackets');
    }
    const isBlobs = input.every(isBlobLike);
    const isPx    = !isBlobs && input.every(isImageDataLike);
    if (!isBlobs && !isPx) {
        throw new Error('mergeBrackets: invalid input — expected Blob[] or ImageData-like[]');
    }
    if (isBlobs) {
        for (const b of input) {
            if (b.type && !SUPPORTED_MIME.has(b.type)) {
                throw new Error(`mergeBrackets: unsupported MIME ${b.type}`);
            }
        }
    }

    const signal = opts.signal;

    if (isBlobs) {
        return mergeFromBlobs(input, opts, signal);
    }
    return mergeFromPixels(input, opts, signal);
}

/**
 * Blob path — decode + ingest + EV-normalise + fuse.
 */
async function mergeFromBlobs(blobs, opts, signal) {
    const useExif = opts.useExif !== false;

    /* Step 1: try EXIF first if requested. */
    let evStops = null;
    if (useExif) {
        evStops = await tryExifEVStops(blobs, signal);
    }

    /* Step 2: ingest each blob sequentially. */
    checkAbort(signal);
    const stack = [];
    let firstW = 0, firstH = 0;
    let allJpeg = true, allPng = true;
    for (let i = 0; i < blobs.length; i++) {
        checkAbort(signal);
        const blob = blobs[i];
        const { rgba, width, height } = await ingestBlob(blob);
        if (i === 0) {
            firstW = width; firstH = height;
        } else if (width !== firstW || height !== firstH) {
            throw new Error('mergeBrackets: bracket dimensions mismatch');
        }
        if (blob.type !== 'image/jpeg' && blob.type !== 'image/jpg') allJpeg = false;
        if (blob.type !== 'image/png') allPng = false;
        stack.push({ rgba, width, height });
    }

    /* Step 3: if EXIF didn't yield EVs, estimate from pixels. Track the
     * fact that we fell back so detectFusionMode (Codex 2.A.2.fix HIGH-1)
     * can still see the missing-EXIF signal even though evStops is now
     * finite from the estimator. */
    const exifMissing = !evStops || !evStops.every(Number.isFinite);
    if (exifMissing) {
        evStops = estimateEVStops(stack);
    }

    return fusePostProcess(stack, evStops, exifMissing, opts, signal,
        allJpeg ? 'jpeg' : (allPng ? 'png' : 'mixed'));
}

/**
 * Pixel-stack path — accepts ImageData-like brackets. Used by tests and
 * by callers that have already decoded their sources upstream.
 */
async function mergeFromPixels(stack, opts, signal) {
    let firstW = 0, firstH = 0;
    const float = [];
    for (let i = 0; i < stack.length; i++) {
        checkAbort(signal);
        const b = stack[i];
        if (i === 0) {
            firstW = b.width; firstH = b.height;
        } else if (b.width !== firstW || b.height !== firstH) {
            throw new Error('mergeBrackets: bracket dimensions mismatch');
        }
        if (b.rgba instanceof Float32Array) {
            /* Codex HDR-A review fix: caller-owned Float32 must be cloned —
             * fusePostProcess mutates entries in place during EV scale and
             * we MUST NOT scribble on the caller's buffer. */
            float.push({ rgba: new Float32Array(b.rgba), width: b.width, height: b.height });
        } else {
            const rgba = ingestSRGB(b.data, b.width, b.height);
            float.push({ rgba, width: b.width, height: b.height });
        }
    }
    /* Pixel path always reaches us via the estimator (no EXIF channel
     * here) — flag exifMissing=true so detection can pick up signal #1. */
    const evStops = estimateEVStops(float);
    return fusePostProcess(float, evStops, true, opts, signal, 'pixels');
}

/**
 * EV-normalise + fuse, common tail used by both blob and pixel paths.
 *
 * Memory: we MUTATE `stack` entries in place with their normalised buffers
 * (or reuse the original buffer if EV delta == 0) — this is safe because
 * `stack` is internal-only and never returned to the caller. Avoids
 * doubling Float32 memory at the worst possible moment.
 */
function fusePostProcess(stack, evStops, exifMissing, opts, signal, source) {
    checkAbort(signal);
    /* Apply 2^(-EV) scale per bracket. Skip allocation when delta == 0:
     * those buffers belong to us (came out of ingestSRGB) so we leave
     * them as-is. For non-zero delta we still need a new buffer because
     * we don't own the input shape semantics — but we drop the original
     * reference immediately so GC can free pre-fuse Float32 allocations. */
    for (let k = 0; k < stack.length; k++) {
        const evDelta = -evStops[k];
        if (evDelta !== 0 && Number.isFinite(evDelta)) {
            const src = stack[k].rgba;
            const factor = Math.pow(2, evDelta);
            /* Mutate-in-place: stack is internal scratch so this is safe. */
            for (let i = 0; i < src.length; i += 4) {
                src[i]     *= factor;
                src[i + 1] *= factor;
                src[i + 2] *= factor;
                /* alpha untouched */
            }
        }
    }

    /* Phase 2.A.2: resolve fusionMode='auto' BEFORE entering fuseBrackets
     * (Codex HIGH-1: detection must be shared between merge.js and the UI
     * path). Caller-supplied 'camera'/'robust' bypass detection. Default
     * for the public API stays 'camera' (Codex D2 — UI flips this to
     * 'auto' via hdr-merge-modal.js). */
    let resolvedMode = opts.fusionMode;
    let detection = null;
    if (resolvedMode === 'auto') {
        checkAbort(signal);
        /* Codex 2.A.2.fix HIGH-1: pass explicit exifMissing flag because
         * the estimator step above always replaces missing EXIF with
         * finite estimates — detection cannot infer this from evStops. */
        detection = detectFusionMode(stack, { evStops, exifMissing: !!exifMissing });
        resolvedMode = detection.mode;
    } else if (resolvedMode !== 'robust' && resolvedMode !== 'camera') {
        resolvedMode = 'camera';
    }

    checkAbort(signal);
    const fused = fuseBrackets(stack, {
        levels:     opts.levels,
        contrast:   opts.contrast,
        saturation: opts.saturation,
        exposure:   opts.exposure,
        fusionMode: resolvedMode,
        /* Codex R1: forward EV stops so fuseBrackets picks the median-EV
         * bracket as guide for the guided-filter pass. evStops here is
         * the ORIGINAL bracket EVs (pre-scale) — exactly what guide
         * selection wants. */
        evStops,
        muEV:       opts.muEV,
        sigEV:      opts.sigEV,
        evFloor:    opts.evFloor,
        gfRadius:   opts.gfRadius,
        gfEps:      opts.gfEps,
        skipGuidedFilter: opts.skipGuidedFilter,
        signal,
    });

    /* Help GC drop the source brackets before we return. */
    for (let k = 0; k < stack.length; k++) stack[k].rgba = null;

    return {
        rgba:    fused.rgba,
        width:   fused.width,
        height:  fused.height,
        evStops,
        source,
        /* Phase 2.A.2: resolved mode + detection diagnostics (only when
         * the caller asked for 'auto'; otherwise detection is null). UI
         * uses this to show "robust mode auto-detected" toast + override. */
        fusionMode: resolvedMode,
        autoDetect: detection,
    };
}

/* ── Decoding helpers ────────────────────────────────────────────────── */

/**
 * Decode a single blob to Uint8 sRGB, then ingest to ACEScg linear Float32.
 * Uses createImageBitmap + OffscreenCanvas — works in workers and main.
 */
async function ingestBlob(blob) {
    /* createImageBitmap honours EXIF Orientation by default in modern
     * browsers; we explicitly request "from-image" to keep behaviour
     * predictable across engines. */
    const bitmap = await createImageBitmap(blob, {
        imageOrientation: 'from-image',
        premultiplyAlpha: 'none',
        colorSpaceConversion: 'none',
    });
    try {
        const { width, height } = bitmap;
        if (!width || !height) throw new Error('mergeBrackets: zero-sized bitmap');
        const ctx = makeOffscreenCtx(width, height);
        ctx.drawImage(bitmap, 0, 0);
        const imgData = ctx.getImageData(0, 0, width, height);
        const rgba = ingestSRGB(imgData.data, width, height);
        return { rgba, width, height };
    } finally {
        if (typeof bitmap.close === 'function') bitmap.close();
    }
}

/**
 * Build a 2D context on an OffscreenCanvas (worker-friendly) or fallback
 * to a regular canvas if OffscreenCanvas isn't available.
 */
function makeOffscreenCtx(w, h) {
    if (typeof OffscreenCanvas !== 'undefined') {
        const c = new OffscreenCanvas(w, h);
        const ctx = c.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' });
        if (!ctx) throw new Error('mergeBrackets: OffscreenCanvas 2d context unavailable');
        return ctx;
    }
    /* Document-side fallback. globalThis.document keeps ESM purity. */
    const doc = globalThis.document;
    if (!doc) throw new Error('mergeBrackets: no canvas runtime available');
    const c = doc.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' });
    if (!ctx) throw new Error('mergeBrackets: 2d context unavailable');
    return ctx;
}

/**
 * Try to read EXIF EVs for every blob. Returns Float32Array normalised
 * to median=0, OR null if any blob is missing an absolute EV value.
 * (We keep an all-or-nothing rule — partial EXIF would be misleading.)
 */
async function tryExifEVStops(blobs, signal) {
    const abs = new Array(blobs.length);
    for (let i = 0; i < blobs.length; i++) {
        checkAbort(signal);
        abs[i] = await parseExifEV(blobs[i]);
        if (abs[i] == null || !Number.isFinite(abs[i])) return null;
    }
    /* Normalise to median = 0. */
    const sorted = [...abs].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    /* NB: higher EV = scene WAS brighter, so the bracket carries LESS
     * exposure (camera stopped down). Relative EV used by the fuser is
     * a "scene-brightness" offset — we return it directly; downstream
     * fusePostProcess scales by 2^(-EV) so brighter-scene brackets get
     * pulled DOWN to the same reference. */
    const out = new Float32Array(blobs.length);
    for (let i = 0; i < blobs.length; i++) out[i] = abs[i] - median;
    return out;
}

function checkAbort(signal) {
    if (signal && signal.aborted) {
        const err = new Error('mergeBrackets: aborted');
        err.name = 'AbortError';
        throw err;
    }
}
